import { LicenseManager } from '@ag-grid-enterprise/all-modules';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, EventEmitter, OnInit, ViewChild } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { environment } from '@app/environment';
import { Globals } from '@app/shared/models';
import { ResearchAuthService } from '@core/authentication/index';
import { UserAccess } from '@core/authorization/user-access';
import { AppInsightsService } from '@core/services';
import { ConfirmDialogComponent, DialogBase, DialogButton, DialogIcon, Dialogs, IConfirmDialogOptions } from '@m27/common';
import { ReportingDialogsComponent } from '@reporting/dialogs/dialog.component';
import { IUser } from '@shared/interfaces/index';
import { MainMenuService } from '@shared/services/main-menu.service';
import { AppDialogsComponent } from './dialogs/dialog.component';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss']
})
export class AppComponent implements OnInit {

    public runEnvironment = '...';
    public version = '...';
    public copyright = `© ${(new Date()).getFullYear()} Corteva`;

    private localStorageKey = 'myUserInfo';
    public isLoggedIn = false;
    public loading = true;
    public onLoggedIn: EventEmitter<false> = new EventEmitter<false>();
    public userAccess: UserAccess = Globals.UAC;
    public loggedInUserDisplayName = '';
    public loggedInUserAvatar: string = null;
    private loginDialog: ConfirmDialogComponent = null;
    public loggedInUserRoles: string[] = [];
    public loggedInUserEmail = '';
    public loggedInUserPhone = '';
    public loggedInUserLastName = '';
    public loggedInUserFirstName = '';

    @ViewChild(AppDialogsComponent, { static: true }) AppDialogs: AppDialogsComponent;
    @ViewChild(ReportingDialogsComponent, { static: true }) ReportingDialogs: ReportingDialogsComponent;

    constructor(private auth: ResearchAuthService,
                private titleService: Title,
                private router: Router,
                private mainMenuService: MainMenuService,
                appInsightsService: AppInsightsService) {

        this.titleService.setTitle('Gatekeeper • ' + environment.env);

        this.runEnvironment = environment.env;
        this.version = environment.version;

        auth.onUnathenticatedResponse().subscribe((response: HttpErrorResponse) => {
            localStorage.removeItem(this.localStorageKey);
            this.isLoggedIn = false;

            if (this.router.url.toLowerCase() !== '/login') {
                const options: IConfirmDialogOptions = {
                    Title: 'Application Login',
                    Header: 'Click the button below to log in to the application.',
                    Body: 'You may be prompted for your Corteva&trade; credentials.',
                    Buttons: [
                        new DialogButton({
                            Text: 'Login',
                            IsPrimary: () => true,
                            Action: (dialogBase: DialogBase, isChecked: boolean) => {
                                window.addEventListener('message', this.silentRefreshPostMessageEventListener, false);
                                this.auth.login();
                            }
                        } as DialogButton)
                    ] as DialogButton[],
                    Icon: DialogIcon.Lock,
                    ShowFullScreenButton: false
                };

                this.loginDialog = Dialogs.Confirm.open(options);
            }
        });

        window.addEventListener('storage', this.localStorageEventListener, false);

        let afterLoginUrl = window.location.pathname;

        if (afterLoginUrl.toLowerCase() === '/login') {
            afterLoginUrl = '/home';
        }

        auth.initialize({
            loginRoute: '/login',
            afterLoginRoute: afterLoginUrl,
            afterLogoutRoute: '/home',
            signInUrl: environment.signInUrl,
            signOutUrl: environment.signOutUrl,
            userInfoUrl: environment.refreshUrl,
            extendSessionDelay: 600,
            initializeClaims: () => {
                const data = localStorage.getItem(this.localStorageKey);

                if (data !== undefined && data !== null) {
                    this.setUserClaims(JSON.parse(data) as IUser);
                }
                return data;
            },
            getClaims: (response: any) => {
                const shouldNavigate = true;
                this.setUserClaims(response as IUser);

                if (response === null) {
                    this.login();
                    return null;
                }

                localStorage.setItem(this.localStorageKey, JSON.stringify(Globals.CurrentUser));

                if (shouldNavigate) {
                    router.navigate([auth.afterLoginRoute]);
                }

                return Globals.CurrentUser;
            }
        });

        if (environment.instrumentationKey !== undefined && environment.instrumentationKey !== null) {
            appInsightsService.initialize(environment.instrumentationKey);
        }
    }

    public isAuthenticated(): boolean {
        return this.isLoggedIn;
    }

    public login(): void {
        this.auth.login();
    }

    public logout(): void {
        this.isLoggedIn = false;
        localStorage.removeItem(this.localStorageKey);
        this.auth.logout();
    }

    private localStorageEventListener: EventListener = (e: StorageEvent) => {
        const key = e.key;
        // this key needs to match what is set in logout.html
        if (key === 'wipeMe') {
            localStorage.removeItem('wipeMe');
            this.logout();
        }
    }

    private setUserClaims(claims: IUser): void {
        const roles = 'Roles';

        Globals.UAC.Initialize(claims[roles]);

        this.loggedInUserRoles = claims[roles];

        delete claims[roles];

        Globals.CurrentUser = claims;
        this.onLoggedIn.emit();

        this.isLoggedIn = true;

        this.loggedInUserEmail = Globals.CurrentUser.Email;
        this.loggedInUserDisplayName = Globals.CurrentUser.DisplayName;
        this.loggedInUserPhone = Globals.CurrentUser.Phone;
        this.loggedInUserAvatar = Globals.CurrentUser.HeadshotImage;
        this.loggedInUserFirstName = Globals.CurrentUser.FirstName;
        this.loggedInUserLastName = Globals.CurrentUser.LastName;
    }

    private silentRefreshPostMessageEventListener: EventListener = (e: MessageEvent) => {
        window.removeEventListener('message', this.silentRefreshPostMessageEventListener);
        const message: string = e.data;

        if (message === 'closeMe') {
            this.loginDialog.close();
            this.auth.refresh();
        }
    }

    public GetUnreadReportCount(): number {
        return Object.keys(this.mainMenuService.unviewedReports).filter((key) => {
            return this.mainMenuService.unviewedReports[key];
        }).length;
    }

    ngOnInit(): void {

        Globals.AppDialogs = this.AppDialogs;
        Globals.ReportingDialogs = this.ReportingDialogs;

        Globals.AppDialogs.Login.onClose.subscribe(() => {
            this.auth.refresh();
        });

        LicenseManager.setLicenseKey('CompanyName=SHI International Corp_on_behalf_of_Pioneer Hi-Bred Intl Inc,LicensedGroup=DSI,LicenseType=MultipleApplications,LicensedConcurrentDeveloperCount=25,LicensedProductionInstancesCount=0,AssetReference=AG-010239,ExpiryDate=3_September_2021_[v2]_MTYzMDYyMzYwMDAwMA==03251088b9c083133d340b322983c96d');
        //this.onLoggedIn.subscribe(() => {

        //});
    }
}
